import { inject, untracked } from '@angular/core';
import { signalStore, type, withProps, patchState } from '@ngrx/signals';
import {
  entityConfig,
  withEntities,
  updateEntity,
} from '@ngrx/signals/entities';
import {
  withCallStatus,
  withEntitiesCalls,
  withEntitiesLoadingCall,
  withLogger,
  withCalls,
} from '@ngrx-traits/signals';
import { Suggestion, SuggestionService } from '..';
import { EMPTY, map, first } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

interface SuggestionCategoryEntity {
  id: string;
  categoryId: number;
  suggestions: Suggestion[];
}

const suggestionEntity = entityConfig({
  entity: type<SuggestionCategoryEntity>(),
  collection: 'suggestionCategory',
  selectId: (entity: SuggestionCategoryEntity) => entity.id,
});

export const SuggestionStore = signalStore(
  withEntities(suggestionEntity),
  withCallStatus(suggestionEntity, {
    initialValue: 'loading',
  }),
  withLogger({
    name: 'SuggestionStore',
    showDiff: true,
  }),
  withProps(() => ({
    _suggestionService: inject(SuggestionService),
  })),
  withEntitiesLoadingCall(suggestionEntity, {
    fetchEntities: ({ _suggestionService }) =>
      _suggestionService.loadCategories().pipe(
        map((categories) =>
          categories.map((category) => ({
            id: category.name.toLowerCase(),
            categoryId: category.id,
            suggestions: [],
          }))
        )
      ),
  }),
  withProps(({ isSuggestionCategoryEntitiesLoaded }) => ({
    $isSuggestionCategoryEntitiesLoaded: toObservable(
      isSuggestionCategoryEntitiesLoaded
    ),
  })),
  withEntitiesCalls(
    suggestionEntity,
    ({
      _suggestionService,
      suggestionCategoryEntityMap,
      $isSuggestionCategoryEntitiesLoaded,
    }) => ({
      loadSuggestions: {
        call: (id: string) => {
          const category = untracked(suggestionCategoryEntityMap)[id];
          if (!category) {
            return EMPTY;
          }
          return _suggestionService
            .loadSuggestionsByCategory(category.categoryId)
            .pipe(map((suggestions) => ({ suggestions })));
        },
        skipWhen: () =>
          $isSuggestionCategoryEntitiesLoaded.pipe(
            map((categoriesLoaded) => !categoriesLoaded),
            first()
          ),
        paramsSelectId: (id: string) => id,
      },
    })
  ),
  withCalls((store) => ({
    addSuggestion: {
      call: ({
        categoryName,
        text,
      }: {
        categoryName: string;
        text: string;
      }) => {
        const lookupKey = categoryName.toLowerCase();
        const category = store.suggestionCategoryEntityMap()[lookupKey];

        if (!category) {
          throw new Error(`Category '${categoryName}' not found`);
        }
        return store._suggestionService
          .addSuggestion(category.categoryId, text)
          .pipe(
            map(() => {
              const newSuggestion = {
                id: Date.now(),
                text,
                categoryId: category.categoryId,
              };

              patchState(
                store,
                updateEntity(
                  {
                    id: lookupKey,
                    changes: {
                      suggestions: [...category.suggestions, newSuggestion],
                    },
                  },
                  suggestionEntity
                )
              );
              return newSuggestion;
            })
          );
      },
      resultProp: 'addSuggestionResult',
      storeResult: false,
      mapError: (error) => {
        return error;
      },
    },

    removeSuggestion: {
      call: ({
        categoryName,
        suggestionId,
      }: {
        categoryName: string;
        suggestionId: number;
      }) => {
        const lookupKey = categoryName.toLowerCase();
        const category = store.suggestionCategoryEntityMap()[lookupKey];

        if (!category) {
          throw new Error(`Category '${categoryName}' not found`);
        }

        return store._suggestionService
          .removeSuggestion(category.categoryId, suggestionId)
          .pipe(
            map(() => {
              patchState(
                store,
                updateEntity(
                  {
                    id: lookupKey,
                    changes: {
                      suggestions: category.suggestions.filter(
                        (s) => s.id !== suggestionId
                      ),
                    },
                  },
                  suggestionEntity
                )
              );
            })
          );
      },
      resultProp: 'removeSuggestionResult',
      storeResult: false,
      mapError: (error) => {
        return error;
      },
    },
  }))
);
